import './coapplicantSameResidencePage.scss';

import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import { toCurrencyString } from '@thesavingsgroup/utils/toCurrencyString';
import { toNumericString } from '@thesavingsgroup/utils/toNumericString';
import isEmpty from 'lodash/isEmpty';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { NumberInput } from '../../../components/NumberInput/NumberInput';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { next, previous } = useWorkflowStore();
  const { t } = useTranslation();

  const {
    computed: { isCoapplicantPersisted },
    coapplicantCurrentResidenceMonthlyPaymentAmount,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentResidenceMonthlyPaymentAmount,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      housingAmountValue: toCurrencyString(
        coapplicantCurrentResidenceMonthlyPaymentAmount,
      ),
    },
    delayError: 200,
    mode: 'onTouched',
    shouldFocusError: false,
  });

  const housingAmountValidation = {
    setValueAs: (value: string): string => toNumericString(value),
    required: t('validation.housingAmount.required'),
    min: {
      value: 0,
      message: t('validation.housingAmount.min', { min: '$0' }),
    },
    max: {
      value: 1000000,
      message: t('validation.housingAmount.max', { max: '$1,000,000' }),
    },
  };

  const previousStep = (): void => {
    navigate(previous());
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { housingAmountValue } = values;

    if (!housingAmountValue) {
      return;
    }

    setCoapplicantCurrentResidenceMonthlyPaymentAmount(housingAmountValue);

    if (isCoapplicantPersisted && isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="coapplicant-same-residence-page" sx={theme.pageLayout}>
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantSameResidence.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.coapplicantSameResidence.form.h1}>
                {t('CoapplicantSameResidencePage.title')}
              </h1>
              <h2 data-testid={locator.pages.coapplicantSameResidence.form.h2}>
                {t('CoapplicantSameResidencePage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert severity="error">
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <NumberInput
                  {...register('housingAmountValue', housingAmountValidation)}
                  data-testid={
                    locator.pages.coapplicantSameResidence.form
                      .housingAmountInput
                  }
                  error={errors.housingAmountValue}
                  errorTestId={
                    locator.pages.coapplicantSameResidence.form
                      .housingAmountInputError
                  }
                  label={t(
                    'CoapplicantSameResidencePage.coapplicantHousingAmount',
                  )}
                  currency
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  data-testid={
                    locator.pages.coapplicantSameResidence.form.submitButton
                  }
                  disabled={!isValid}
                  loader={isSyncingCoapplicant}
                  fullWidth
                  submit
                >
                  {t('CoapplicantSameResidencePage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={locator.pages.coapplicantSameResidence.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantSameResidencePage';
